import { useEffect, useState } from 'react'
import { viewerApi } from '@viewer/lib/viewerApi'
import { type KeyValue, type PsetGroup, getPsetsAndQsets } from '@viewer/lib/ifcItemData'
import type { UnidadeDaPropriedade } from '@viewer/lib/unidades'
import KeyValueTable from './KeyValueTable'

/** Os property sets do type de uma instância.
 *
 *  No IFC, uma instância herda as propriedades do seu type: o que está
 *  no IfcColumnType vale para todos os pilares desse type, e a instância
 *  só repete o que muda. Mostrar só as da instância escondia metade da
 *  informação do elemento. */
export default function PsetsDoType({
  modelId,
  typeId,
  nomesDaInstancia,
  unidadeDe,
}: {
  modelId: string
  typeId: number
  /** Os nomes dos property sets da instância: um com o mesmo nome no
   *  type fica marcado, porque a instância sobrepõe-se a ele. */
  nomesDaInstancia: Set<string>
  unidadeDe: (row: KeyValue) => UnidadeDaPropriedade | null
}) {
  const [carga, setCarga] = useState<{ chave: string; psets: PsetGroup[] | null }>({ chave: '', psets: null })
  const chave = `${modelId}:${typeId}`

  useEffect(() => {
    const model = viewerApi.getModel(modelId)
    if (!model) return
    let vivo = true
    model
      .getItemsData([typeId], {
        attributesDefault: true,
        relationsDefault: { attributes: true, relations: false },
        relations: { HasPropertySets: { attributes: true, relations: true } },
      })
      .then(([t]) => {
        if (!vivo) return
        const { psets, qsets } = getPsetsAndQsets({ IsDefinedBy: (t as Record<string, unknown>)?.HasPropertySets })
        setCarga({ chave: `${modelId}:${typeId}`, psets: [...psets, ...qsets] })
      })
      .catch(() => {
        if (vivo) setCarga({ chave: `${modelId}:${typeId}`, psets: [] })
      })
    return () => {
      vivo = false
    }
  }, [modelId, typeId])

  const psets = carga.chave === chave ? carga.psets : null
  if (psets === null) return <div className="mt-2 text-[11px] text-[var(--color-text-muted)]">A ler as propriedades do type…</div>
  return (
    <div className="mt-2">
      <div className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
        Property sets do type ({psets.length})
      </div>
      {psets.length === 0 ? (
        <div className="text-[11px] text-[var(--color-text-muted)]">O type não tem property sets.</div>
      ) : (
        psets.map((g, i) => (
          <div key={`${g.name}-${i}`} className="mb-2 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40">
            <div className="flex items-center gap-2 border-b border-[var(--color-border)]/60 px-2 py-1 text-[11px] font-medium text-[var(--color-text)]">
              <span>{g.name}</span>
              <span className="text-[10px] text-[var(--color-text-muted)]">do type</span>
              {nomesDaInstancia.has(g.name) && (
                <span
                  className="text-[10px] text-[var(--color-warn-text)]"
                  title="A instância tem um property set com o mesmo nome. Onde as propriedades coincidem, vale o valor da instância."
                >
                  também na instância
                </span>
              )}
            </div>
            <div className="px-2 pb-1">
              <KeyValueTable rows={g.rows} unidadeDe={unidadeDe} emptyMessage="Sem propriedades." />
            </div>
          </div>
        ))
      )}
    </div>
  )
}
